import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { handleAuthentication, showSnackBar } from "@containers/App/reducer"
import { parsePageInfoWidget, parseWidgetData, parseDialogData } from "./parser"
import { getSource, prefixCartTypeIntoUrl } from "@commonUtils"
import axios from "axios"

const initialState = {
    isLoading: false,
    isFetched: false,
    error: null,
    widgets: {},
    pageInfoWidgets: {},
    selectedOptionId: null,
    etaSelectionData: {},
    listOfItems: {},
    paymentMethods: {},
    cardInfo: null,
    sendOtpData: null,
    isOtpSent: false,
    dialogInfoWidget: {},
    shouldFetchOffer: false,
}

export const cartPaymentPageSlice = createSlice({
    name: "cartPaymentPage",
    initialState: initialState,
    reducers: {
        reset: () => initialState,
        setPaymentMethodData: (state, action) => {
            state.paymentMethods[action.payload?.activeId].items = action.payload?.data
        },
        setDefaultCartInfoState: (state) => {
            state.cardInfo = null
        },
        resetWalletData: (state) => {
            state.cardsendOtpDataInfo = null
            state.isOtpSent = false
        },
    },
    extraReducers: (builder) => {
        //handle asynchronous actions
        builder.addCase(fetchPaymentData.fulfilled, (state, action) => {
            state.isLoading = false
            state.isFetched = true
            state.error = false
            let data = parseWidgetData(action.payload)
            state.analyticsData = data?.analyticsData
            state.widgets = data.widgetData
            state.paymentMethods = data.paymentMethods
            state.pageInfoWidgets = parsePageInfoWidget(action.payload)
            state.defaultSelectedMethodData = data?.defaultSelectedMethodData
            state.recommendedMethods = data?.recommendedMethods
            state.dialogInfoWidget = parseDialogData(
                action.payload.dialog_data,
                action.payload.place_order_request
            )
            state.shouldFetchOffer = data.shouldFetchOffer
            state.mixPanelData = action.payload?.mixpanel_data
        }),
            builder.addCase(fetchPaymentData.pending, (state) => {
                state.isLoading = true
                state.isFetched = false
                state.error = false
            }),
            builder.addCase(fetchPaymentData.rejected, (state) => {
                state.isLoading = false
                state.error = true
                state.isFetched = false
            })
        builder.addCase(fetchCardInfo.fulfilled, (state, action) => {
            state.cardInfo = action.payload
        }),
            builder.addCase(fetchCardInfo.rejected, (state, action) => {
                state.cardInfo = { error: action.payload.error?.message }
            })
        builder.addCase(sendOtpForWalletLinking.fulfilled, (state, action) => {
            state.sendOtpData = action.payload
            state.isOtpSent = true
        })
    },
})

export const fetchPaymentData = createAsyncThunk(
    "cartPaymentPageSlice/fetchPaymentPageData",
    async (_arg, { extra, rejectWithValue, dispatch, getState }) => {
        const { api } = extra
        const errorCallback = _arg?.errorCallback
        const successCallback = _arg?.successCallback
        const navigate = _arg?.navigate
        const methodsPayload = _arg?.methodsPayload
        const cartType = getState().shellReducer.cartType

        const payload = {
            payment_supported_apps: { upi: [] },
            cart_info: {
                types: cartType,
            },
            source: getSource(cartType),
            ...methodsPayload,
        }

        try {
            const res = await api.post(`api/v4/payment/methods`, payload)
            if (typeof successCallback === "function") successCallback(res.data)
            return Promise.resolve(res.data)
        } catch (err) {
            const onServer = typeof window === "undefined"
            if (!onServer && (err.status_code === 403 || err.status_code === 401))
                dispatch(handleAuthentication())

            if (err.status_code === 400) {
                if (err.error?.message) showSnackBar(dispatch, err.error.message)
                if (err.error?.errors?.[0]?.target_url) {
                    navigate(err.error?.errors?.[0]?.target_url)
                } else {
                    document.location.replace(prefixCartTypeIntoUrl("/"))
                }
            }

            if (err.error?.errors) {
                if (typeof errorCallback === "function") errorCallback?.(err)
            }
            return rejectWithValue(err)
        }
    }
)
export const fetchOffer = createAsyncThunk(
    "cartPaymentPageSlice/fetchPaymentOfferData",
    async (_arg, { extra, rejectWithValue, dispatch }) => {
        const { api } = extra
        const { payload } = _arg
        const successCallback = _arg?.successCallback
        try {
            const res = await api.post(`api/v4/carts/payment/offer/apply`, payload)
            if (typeof successCallback === "function") successCallback(res.data)
            return Promise.resolve(res.data)
        } catch (err) {
            const onServer = typeof window === "undefined"
            if (err?.status_code !== 403 || err?.status_code !== 401) {
                showSnackBar(dispatch, err.error.message)
            }
            if (!onServer && (err.status_code === 403 || err.status_code === 401))
                dispatch(handleAuthentication())
            return rejectWithValue(err)
        }
    }
)
export const fetchCardInfo = createAsyncThunk(
    "cartPaymentPageSlice/fetchCardData",
    async (_arg, { extra, rejectWithValue, dispatch, getState }) => {
        const { api } = extra
        const { cardNo } = _arg
        const successCallback = _arg?.successCallback
        const cartType = getState().shellReducer.cartType
        try {
            const res = await api.get(`api/v4/cardbins/${cardNo}/info?source=${getSource(cartType)}`)
            await Promise.resolve()
            if (typeof successCallback === "function") successCallback(res.data)
            return Promise.resolve(res.data)
        } catch (err) {
            const onServer = typeof window === "undefined"
            if (!onServer && (err.status_code === 403 || err.status_code === 401))
                dispatch(handleAuthentication())
            return rejectWithValue(err)
        }
    }
)
export const sendOtpForWalletLinking = createAsyncThunk(
    "cartPaymentPageSlice/sendOtpForWalletLinkingData",
    async (_arg, { extra, rejectWithValue, dispatch }) => {
        const { api } = extra
        const { payload } = _arg
        const successCallback = _arg?.successCallback
        try {
            const res = await api.post(`api/v4/wallets`, payload)
            await Promise.resolve()
            if (typeof successCallback === "function") successCallback(res.data)
            return Promise.resolve(res.data)
        } catch (err) {
            const onServer = typeof window === "undefined"
            if (err?.status_code !== 403 || err?.status_code !== 401) {
                showSnackBar(dispatch, err.error.message)
            }
            if (!onServer && (err.status_code === 403 || err.status_code === 401))
                dispatch(handleAuthentication())
            return rejectWithValue(err)
        }
    }
)
export const validateUpiId = createAsyncThunk(
    "cartPaymentPageSlice/validateUpiIdData",
    async (_arg, { extra, rejectWithValue, dispatch }) => {
        const { api } = extra
        const { payload } = _arg
        const successCallback = _arg?.successCallback
        try {
            const res = await api.post(`api/v4/upi/validate`, payload)
            await Promise.resolve()
            if (res.data?.status === "VALID") {
                if (typeof successCallback === "function") successCallback(res.data)
            } else if (res.data?.status === "SKIPPED" || res.data?.status === "INVALID") {
                showSnackBar(dispatch, res.data?.message)
            }
            return Promise.resolve(res.data)
        } catch (err) {
            const onServer = typeof window === "undefined"
            if (err?.status_code !== 403 || err?.status_code !== 401) {
                if (err.error.errors[0].target_url) {
                    window.location.href = err.error.errors[0].target_url
                }
                showSnackBar(dispatch, err.error.message)
            }
            if (!onServer && (err.status_code === 403 || err.status_code === 401))
                dispatch(handleAuthentication())
            return rejectWithValue(err)
        }
    }
)
export const initPayment = createAsyncThunk(
    "cartPaymentPageSlice/initiatePayment",
    async (_arg, { extra, rejectWithValue, dispatch, getState }) => {
        const { api } = extra
        const { payload, navigate } = _arg
        const successCallback = _arg?.successCallback
        const { cartType } = getState().shellReducer
        try {
            const res = await api.post(`api/v4/payment/initiate`, payload)
            await Promise.resolve()

            if (typeof successCallback === "function") successCallback(res.data)
            return Promise.resolve(res.data)
        } catch (err) {
            const onServer = typeof window === "undefined"
            if (err.status_code === 400 && err.error?.message) {
                showSnackBar(dispatch, err.error.message)
                if (err.error.errors?.[0]?.target_url) {
                    navigate(err.error.errors[0].target_url)
                } else {
                    navigate(prefixCartTypeIntoUrl("/cart", cartType), { replace: true })
                }
            } else if (err?.status_code !== 403 && err?.status_code !== 401) {
                showSnackBar(dispatch, err.error.message)
            }
            if (!onServer && (err.status_code === 403 || err.status_code === 401))
                dispatch(handleAuthentication())
            return rejectWithValue(err)
        }
    }
)
export const openRazorpayModal = (data, navigate) => {
    return async (dispatch, getState) => {
        const isScriptLoaded = await loadRazorpaySdk()
        if (!isScriptLoaded) return logger.info("razorpay script is not loaded")
        const cartType = getState().shellReducer.cartType

        const options = {
            key: data.keys?.key,
            amount: data.keys?.amount,
            name: data.keys?.name,
            description: data.keys?.description,
            image: data.keys?.image,
            order_id: data.keys?.order_id,
            handler: (transaction) => {
                const payload = {
                    razorpay_signature: transaction.razorpay_signature,
                    razorpay_payment_id: transaction.razorpay_payment_id,
                    razorpay_order_id: transaction.razorpay_order_id,
                    payment_aggregator: data.payment_aggregator,
                    types: cartType,
                }
                dispatch(
                    mapPaymentWithOrder({
                        payload,
                        orderId: data.order_id,
                        successCallback: (response) => {
                            const redirectPage = response?.redirect_info?.redirect_page
                            if (redirectPage === "ORDER_SUCCESS_PAGE") {
                                navigate(
                                    prefixCartTypeIntoUrl(
                                        `/cart/checkout/success?orderId=${data.order_id}&aggregator=${data.payment_aggregator}`,
                                        cartType
                                    )
                                )
                            } else if (redirectPage === "TRACK_ORDER_PAGE") {
                                window.location.href =
                                    cartType === "labs"
                                        ? `/labs/mybookings?superOrderId=${data?.order_id}`
                                        : `/trackOrder?orderId=${data?.order_id}`
                            } else if (redirectPage === "CART_PAYMENT_PAGE") {
                                navigate(prefixCartTypeIntoUrl("/cart/payment", cartType))
                            } else if (redirectPage === "LABS_TRACK_ORDER_PAGE") {
                                window.location.href =
                                    data?.redirect_info?.web_redirect_url ||
                                    `/labs/mybookings?superOrderId=${data?.order_id}`
                            }
                        },
                    })
                )
            },
            prefill: {
                email: data.keys?.prefill?.email,
                contact: data.keys?.prefill?.contact,
            },
            modal: {
                ondismiss: function () {
                    const payload = {
                        payment_mode: data.payment_aggregator,
                        order_id: data.order_id,
                        txn_amount: data.txn_amount,
                        cashback_availed: 0,
                        types: cartType,
                    }
                    dispatch(
                        mapPaymentWithOrder({
                            payload,
                            orderId: data.order_id,
                            successCallback: (data) => {
                                const redirectPage = data?.redirect_info?.redirect_page
                                if (redirectPage === "ORDER_SUCCESS_PAGE") {
                                    navigate(
                                        prefixCartTypeIntoUrl(
                                            `/cart/checkout/success?orderId=${data.order_id}&aggregator=${data.payment_aggregator}`,
                                            cartType
                                        )
                                    )
                                } else if (redirectPage === "TRACK_ORDER_PAGE") {
                                    window.location.href =
                                        cartType === "labs"
                                            ? `/labs/mybookings?superOrderId=${data?.order_id}`
                                            : `/trackOrder?orderId=${data?.order_id}`
                                } else if (redirectPage === "CART_PAYMENT_PAGE") {
                                    navigate(prefixCartTypeIntoUrl("/cart/payment", cartType))
                                } else if (redirectPage === "LABS_TRACK_ORDER_PAGE") {
                                    window.location.href =
                                        data?.redirect_info?.web_redirect_url ||
                                        `/labs/mybookings?superOrderId=${data?.order_id}`
                                }
                            },
                        })
                    )
                },
            },
        }

        const razorObj = new window.Razorpay(options)
        razorObj.open()
    }
}
const loadRazorpaySdk = () => {
    return new Promise((resolve) => {
        if (window.Razorpay) return resolve(true)
        const script = document.createElement("script")
        script.src = "https://checkout.razorpay.com/v1/checkout.js"

        script.onload = () => {
            resolve(true)
        }
        script.onerror = () => {
            resolve(false)
        }

        document.body.appendChild(script)
    })
}
export const mapPaymentWithOrder = createAsyncThunk(
    "cartPaymentPageSlice/mapPaymentWithOrderData",
    async (_arg, { extra, rejectWithValue, dispatch }) => {
        const { api } = extra
        const { payload, orderId } = _arg
        const successCallback = _arg?.successCallback
        try {
            const res = await api.post(`api/v4/orders/${orderId}/map-payments`, payload)
            await Promise.resolve()
            if (typeof successCallback === "function") successCallback(res.data)
            return Promise.resolve(res.data)
        } catch (err) {
            const onServer = typeof window === "undefined"
            if (err?.status_code !== 403 || err?.status_code !== 401) {
                showSnackBar(dispatch, err.error.message)
            }
            if (!onServer && (err.status_code === 403 || err.status_code === 401))
                dispatch(handleAuthentication())
            return rejectWithValue(err)
        }
    }
)

/**
 * Poll payment status after specified interval
 * @param {string} aggregator
 * @param {string} paymentOrderId
 * @param {number} attemptNo
 * @param {function} setAttemptNo
 * @return {function} anonymous function
 */

export const pollPaymentStatus = createAsyncThunk(
    "cartPaymentPageSlice/pollPaymentStatus",
    async (_arg, { extra, rejectWithValue, dispatch }) => {
        const { api } = extra
        const { paymentOrderId, attemptNo, aggregator } = _arg
        const successCallback = _arg?.successCallback
        try {
            const res = await api.get(
                `/api/v4/payment/orders/${paymentOrderId}/poll_status?attempt=${attemptNo}&payment_aggregator=${aggregator}`
            )
            await Promise.resolve()
            if (typeof successCallback === "function") successCallback(res.data)
            return Promise.resolve(res.data)
        } catch (err) {
            const onServer = typeof window === "undefined"
            if (err?.status_code !== 403 || err?.status_code !== 401) {
                showSnackBar(dispatch, err.error.message)
            }
            if (!onServer && (err.status_code === 403 || err.status_code === 401))
                dispatch(handleAuthentication())
            return rejectWithValue(err)
        }
    }
)

/**
 * Generate token and initiate payment
 * @param {string} url
 * @param {object} payload
 * @param {object} initData
 * @param {function} navigate
 * @param {string} orderId
 * @return {function} anonymous function
 */

export const generateToken = (dispatch, _arg) => {
    const { url, payload } = _arg
    const formData = new FormData()
    for (const key in payload) {
        formData.append(key, payload[key])
    }
    const successCallback = _arg?.successCallback
    return new Promise((resolve, reject) => {
        return axios
            .post(url, formData)
            .then((res) => {
                if (typeof successCallback === "function") successCallback(res.data)
                return resolve(res.data)
            })
            .catch(({ response: { data: err } }) => {
                const onServer = typeof window === "undefined"
                if (err?.error_info) {
                    showSnackBar(dispatch, err?.error_info?.user_message || err?.error_message)
                }
                if (!onServer && (err.status_code === 403 || err.status_code === 401))
                    dispatch(handleAuthentication())
                return reject(err)
            })
    })
}

/**
 * link user wallet
 * @param {object} payload
 * @param {function} navigate
 * @return {function} anonymous function
 */

export const linkUserWallet = createAsyncThunk(
    "cartPaymentPageSlice/linkUserWallet",
    async (_arg, { extra, rejectWithValue, dispatch }) => {
        const { api } = extra
        const { payload } = _arg
        const successCallback = _arg?.successCallback
        try {
            const res = await api.post(`api/v4/wallets/link`, payload)
            await Promise.resolve()
            if (typeof successCallback === "function") successCallback(res.data)
            return Promise.resolve(res.data)
        } catch (err) {
            const onServer = typeof window === "undefined"
            if (err?.status_code !== 403 || err?.status_code !== 401) {
                showSnackBar(dispatch, err.error.message)
            }
            if (!onServer && (err.status_code === 403 || err.status_code === 401))
                dispatch(handleAuthentication())
            return rejectWithValue(err)
        }
    }
)

/**
 * Recharge and pay in case of order amount > wallet balance
 * @param {object} payload
 * @param {function} navigate
 * @param {string} orderId
 * @return {function} anonymous function
 */

export const rechargeAndPay = createAsyncThunk(
    "cartPaymentPageSlice/rechargeAndPay",
    async (_arg, { extra, rejectWithValue, dispatch }) => {
        const { api } = extra
        const { payload } = _arg
        const successCallback = _arg?.successCallback
        try {
            const res = await api.post(`api/v4/wallets/recharge`, payload)
            await Promise.resolve()
            if (typeof successCallback === "function") successCallback(res.data)
            return Promise.resolve(res.data)
        } catch (err) {
            const onServer = typeof window === "undefined"
            if (err?.status_code !== 403 || err?.status_code !== 401) {
                showSnackBar(dispatch, err.error.message)
            }
            if (!onServer && (err.status_code === 403 || err.status_code === 401))
                dispatch(handleAuthentication())
            return rejectWithValue(err)
        }
    }
)

export const cartPaymentPageReducer = cartPaymentPageSlice.reducer
export const { reset, setPaymentMethodData, setDefaultCartInfoState, resetWalletData } =
    cartPaymentPageSlice.actions
